import { DuRoleEnum } from '../enum/DuRoleEnum';
import { EnergySharingResponse } from './EnergySharingResponse';
import { HeadPointResponse } from './HeadPointResponse';
import { Info } from 'luxon';
import { IsDeletedEnum } from '../enum/IsDeletedEnum';
import { MeterTypeEnum } from '../enum/MeterTypeEnum';
import { RemoteCapabilityEnum } from '../enum/RemoteCapabilityEnum';
import { SDPCategoryEnum } from '../enum/SDPCategoryEnum';
import { SDPDirectionEnum } from '../enum/SDPDirectionEnum';
import { SDPDivisionEnum } from '../enum/SDPDivisionEnum';
import { SDPReadingConsentEnum } from '../enum/SDPReadingConsentEnum';
import { SDPReadingNicheEnum } from '../enum/SDPReadingNicheEnum';
import { SDPStatusEnum } from '../enum/SDPStatusEnum';
import { SDPTariffChoiceEnum } from '../enum/SDPTariffChoiceEnum';
import { SDPTimeFrameEnum } from '../enum/SDPTimeFrameEnum';
import { SDPTypeEnum } from '../enum/SDPTypeEnum';
import { ServiceTypeEnum } from '../enum/ServiceTypeEnum';

export interface SdpResponse {
  id: string;
  hashId: string;
  category?: SDPCategoryEnum;
  type?: SDPTypeEnum;
  division?: SDPDivisionEnum;
  direction?: SDPDirectionEnum;
  mrMonth?: number;
  timeFrame?: SDPTimeFrameEnum;
  readingNiche?: SDPReadingNicheEnum;
  supplier?: string;
  netUserName?: string;
  readingConsent?: SDPReadingConsentEnum;
  room?: string;
  floor?: string;
  initialLoadDate?: string;
  tariffChoice?: SDPTariffChoiceEnum;
  hasBeenConsented?: boolean;
  hasBeenProsumer?: boolean;
  headpoint: HeadPointResponse;
  userHasAccessStartDate?: string;
  userHasAccessEndDate?: string;
  userHasAccessRole?: DuRoleEnum;
  userHasAccessServiceType?: ServiceTypeEnum;
  userHasAccessSfdcConventionId?: string;
  userHasAccessNickname?: string;
  userHasAccessStatus?: SDPStatusEnum;
  userHasAccessIsDeleted?: IsDeletedEnum;
  energySharings?: EnergySharingResponse[];
}

export const headPointPhysicalMeter = (headpoint?: HeadPointResponse) =>
  (headpoint?.physicalMeters?.length === 0) === true
    ? null
    : headpoint?.physicalMeters?.find((ph) => ph.headpointHasMeterEnd == null);

export const headPointNotCapable = (headpoint: HeadPointResponse) => {
  const physicalMeter = headPointPhysicalMeter(headpoint);
  return (
    physicalMeter?.type !== MeterTypeEnum.SMART ||
    physicalMeter?.remoteCapability !== RemoteCapabilityEnum.CAPABLE
  );
};

export const SdpIsNotMandatoryAndConsent = (sdp: SdpResponse) =>
  sdp.readingNiche !== SDPReadingNicheEnum.MANDATORY &&
  sdp.readingConsent !== SDPReadingConsentEnum.YES;

export const SdpHasNickname = (sdp?: SdpResponse | null) => {
  return sdp?.userHasAccessNickname != null && sdp?.userHasAccessNickname!.trim().length > 0;
};

export const SdpFullStatus = (sdp: SdpResponse) =>
  sdp.userHasAccessStatus ?? SDPStatusEnum.INACTIVE;

export const SdpOwnerFilter = (sdp: SdpResponse) =>
  sdp.userHasAccessRole === DuRoleEnum.OWNER &&
  SdpFullStatus(sdp) !== SDPStatusEnum.WAITING &&
  SdpFullStatus(sdp) !== SDPStatusEnum.EXPIRED;

export const SdpOtherFilter = (sdp: SdpResponse) =>
  SdpFullStatus(sdp) !== SDPStatusEnum.EXPIRED &&
  sdp.userHasAccessRole === DuRoleEnum.DELEGATE &&
  SdpFullStatus(sdp) !== SDPStatusEnum.WAITING;

export const SdpWaitingFilter = (sdp: SdpResponse) => SdpFullStatus(sdp) === SDPStatusEnum.WAITING;

export const SdpExpiredFilter = (sdp: SdpResponse) => SdpFullStatus(sdp) === SDPStatusEnum.EXPIRED;

export const SdpIsNotDeleted = (sdp: SdpResponse) =>
  sdp.userHasAccessIsDeleted === IsDeletedEnum.NO || sdp.userHasAccessIsDeleted == null;

export const SdpGetFullMrMonth = (language: string, t: any, sdp?: SdpResponse | null) => {
  return sdp && sdp.mrMonth
    ? Info.months('long', { locale: language })[sdp!.mrMonth - 1]
    : t('common.monthly');
};
